/**
 * @component TopBar
 * @description Obere Navigationsleiste mit Benachrichtigungen, Sprachumschalter,
 * Dark-Mode-Toggle, Benutzermenue und Suchfunktion.
 * Zeigt ungelesene Notifications als Badge an der Glocke.
 *
 * @usage Eingebunden in App.jsx oberhalb des Seiteninhalts
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAppMode } from '../context/AppModeContext';
import { BarChart3, Calendar, Umbrella, Settings, User, Bell, CheckCircle2, Clock, Palmtree, Stethoscope, Receipt, Search, LayoutGrid, Check, Palette, Bug, MessageCircle } from 'lucide-react';
import CommandPalette from './CommandPalette';
import BugReportModal from './BugReportModal';
import SoftphoneWidget from './SoftphoneWidget';
// TimerFloatingPanel mounted globally in App.jsx (#1001) — backdrop-filter on TopBar broke fixed positioning

const API = import.meta.env.VITE_API_URL || '';

function timeAgo(iso, t, locale) {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return t('common.time_ago.just_now');
  const m = Math.floor(s / 60);
  if (m < 60) return t('common.time_ago.minutes', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('common.time_ago.hours', { n: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t('common.time_ago.days', { n: d });
  return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

const typIconMap = {
  'forecast': BarChart3, 'schicht': Calendar, 'abwesenheit': Umbrella, 'system': Settings, 'user': User,
  'urlaub': Palmtree, 'krankenstand': Stethoscope, 'stempel': Clock, 'lohnzettel': Receipt,
};
function getTypIcon(typ) {
  const cat = typ?.split('.')[0];
  const Icon = typIconMap[cat] || Bell;
  return <Icon className="w-4 h-4" />;
}

function BellIcon() {
  return (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function UserAvatar({ userId, initials, size = 8 }) {
  const [imgErr, setImgErr] = useState(false);
  const sizeClass = `w-${size} h-${size}`;
  const src = `${API}/api/users/${userId}/avatar`;

  if (!imgErr) {
    return (
      <img
        src={src}
        alt=""
        className={`${sizeClass} rounded-[10px] object-cover`}
        onError={() => setImgErr(true)}
      />
    );
  }
  return (
    <div className={`${sizeClass} rounded-[10px] flex items-center justify-center`} style={{ background: 'var(--accent-gradient)' }}>
      <span className="text-[10px] font-bold text-white">{initials}</span>
    </div>
  );
}

export default function TopBar({ isDev = false }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, syncToBackend, activeTheme, setActiveThemeById } = useTheme();
  const { t, language, locale, toggleLanguage } = useLanguage();
  const { mode, setMode, canSwitch } = useAppMode();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [toast, setToast] = useState(null);
  const prevCountRef = useRef(0);
  const dropRef = useRef(null);
  const notifRef = useRef(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);
  // v2.308.2 — Theme Quick-Switch (#714)
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [themeList, setThemeList] = useState([]);
  const themeMenuRef = useRef(null);

  // Global Cmd+K / Ctrl+K / slash + Ctrl+Shift+B (Bug-Report) shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(true); return; }
      // Ctrl+Shift+B / Cmd+Shift+B → Bug-Report-Modal (#943)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        if (user?.rolle === 'jv_partner' || user?.rolle === 'portal_kunde') return;
        e.preventDefault(); setBugOpen(true); return;
      }
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) && !e.target.isContentEditable) {
        e.preventDefault(); setCmdOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [user?.rolle]);

  const pollCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/count`);
      if (res.ok) {
        const data = await res.json();
        const newCount = data.ungelesen;
        if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
          setToast(t('topbar.new_notification'));
          setTimeout(() => setToast(null), 3000);
        }
        prevCountRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch { /* ignore */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount((data.notifications || []).filter(n => !n.gelesen).length);
        prevCountRef.current = (data.notifications || []).filter(n => !n.gelesen).length;
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadNotifications();
    const iv = setInterval(pollCount, 60000);
    return () => clearInterval(iv);
  }, [loadNotifications, pollCount]);

  // #727: Persoenliche Tasks Badge (ueberfaellig + heute faellig)
  const pollTasks = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/user-tasks/stats`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setTaskCount((d.ueberfaellig || 0) + (d.heute || 0));
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    pollTasks();
    const iv = setInterval(pollTasks, 60000);
    return () => clearInterval(iv);
  }, [pollTasks]);

  // Email + SMS mode banner (admin/ops_manager/cs_teamlead)
  useEffect(() => {
    if (!user || !['administrator', 'ops_manager', 'cs_teamlead'].includes(user.rolle)) return;
    fetch(`${API}/api/email/send-status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.mode && d.mode !== 'active') setEmailStatus(d); })
      .catch(() => {});
    fetch(`${API}/api/sms/send-status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.mode && d.mode !== 'active') setSmsStatus(d); })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target)) setThemeMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // v2.308.2 — Theme list laden bei Bedarf (#714)
  const loadThemeList = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/themes`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (d?.themes) setThemeList(d.themes);
      }
    } catch { /* ignore */ }
  }, []);

  const handleThemeSelect = async (themeId) => {
    setThemeMenuOpen(false);
    await setActiveThemeById(themeId);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    toggleTheme();
    syncToBackend(newTheme);
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/alle-gelesen`, { method: 'PUT' });
    setNotifications(n => n.map(x => ({ ...x, gelesen: true })));
    setUnreadCount(0);
    prevCountRef.current = 0;
  };

  const markOneRead = async (notif) => {
    if (!notif.gelesen) {
      fetch(`${API}/api/notifications/${notif.id}/gelesen`, { method: 'PUT' }).catch(() => {});
      setNotifications(n => n.map(x => x.id === notif.id ? { ...x, gelesen: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    if (notif.link) {
      navigate(notif.link);
      setNotifOpen(false);
    }
  };

  if (!user) return null;

  const initials = `${user.vorname?.[0] || ''}${user.nachname?.[0] || ''}`.toUpperCase();
  const rolleLabel = t(`topbar.role.${user.rolle}`) !== `topbar.role.${user.rolle}` ? t(`topbar.role.${user.rolle}`) : user.rolle;

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed top-3 right-4 z-[100] animate-slide-in">
          <div className="bg-surface-elevated border border-line rounded-card px-4 py-2.5 shadow-elevated flex items-center gap-2">
            <span className="text-sm">{'\u{1F514}'}</span>
            <span className="text-xs text-content-secondary">{toast}</span>
          </div>
        </div>
      )}

      {/* Demo-Banner (blau, sticky oben) */}
      {window.location.hostname === 'demo.dynaflow.de' && (
        <div className="text-white text-xs font-bold px-4 flex items-center justify-center gap-2 flex-shrink-0" style={{ backgroundColor: '#338edc', height: '32px' }}>
          <span>🎯</span>
          <span className="tracking-wide">{t('topbar.demoBanner')}</span>
        </div>
      )}

      {/* Consolidated status banner (DEV + Email + SMS in one line) — NOT on demo */}
      {(isDev || emailStatus || smsStatus) && window.location.hostname !== 'demo.dynaflow.de' && (
        <div className="bg-amber-500 text-white text-xs font-medium px-4 py-1.5 flex items-center justify-center gap-4 flex-wrap flex-shrink-0">
          {isDev && <span className="font-bold tracking-wide">{t('topbar.devServer')}</span>}
          {emailStatus && (
            <span className="flex items-center gap-1">
              <span className="opacity-75">{emailStatus.mode === 'disabled' ? t('topbar.emailOff') : t('topbar.emailRedirect')}</span>
              {emailStatus.mode !== 'disabled' && <span>{emailStatus.test_address || '?'}</span>}
              <button onClick={() => navigate('/admin/email')} className="underline ml-0.5 opacity-75 hover:opacity-100">
                {emailStatus.mode === 'disabled' ? t('email.bannerActivate') : t('email.bannerChange')}
              </button>
            </span>
          )}
          {smsStatus && (
            <span className="flex items-center gap-1">
              <span className="opacity-75">{smsStatus.mode === 'disabled' ? t('topbar.smsOff') : t('topbar.smsRedirect')}</span>
              {smsStatus.mode !== 'disabled' && <span>{smsStatus.test_redirect || '?'}</span>}
              <button onClick={() => navigate('/einstellungen?tab=sms')} className="underline ml-0.5 opacity-75 hover:opacity-100">
                {smsStatus.mode === 'disabled' ? t('sms.bannerActivate') : t('sms.bannerChange')}
              </button>
            </span>
          )}
        </div>
      )}

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      <div className="h-12 border-b border-line bg-surface-topbar backdrop-blur flex items-center justify-end px-4 gap-2 flex-shrink-0 relative z-50">
        {/* Search / Command Palette */}
        <button
          onClick={() => setCmdOpen(true)}
          className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors"
          title={t('cmdPalette.tooltip')}
        >
          <Search size={16} />
        </button>

        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors text-[11px] font-bold tracking-wide"
          title={language === 'de' ? t('topbar.switchToEnglish') : t('topbar.switchToGerman')}
        >
          {language === 'de' ? 'EN' : 'DE'}
        </button>

        {/* v2.308.2 — Theme Quick-Switch (#714) */}
        <div className="relative" ref={themeMenuRef}>
          <button
            onClick={() => {
              const next = !themeMenuOpen;
              setThemeMenuOpen(next);
              if (next && themeList.length === 0) loadThemeList();
              setDropdownOpen(false);
              setNotifOpen(false);
              setSwitcherOpen(false);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors"
            title={t('topbar.themeSelect')}
          >
            <Palette size={16} />
          </button>
          {themeMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-surface-elevated border border-line-hover rounded-card shadow-elevated z-50 py-1.5 overflow-hidden">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('topbar.themeSelect')}
              </div>
              {themeList.length === 0 ? (
                <div className="px-3 py-3 text-[11px] text-[var(--text-tertiary)]">{t('common.loading')}</div>
              ) : (
                themeList.map(th => {
                  const isActive = activeTheme?.id === th.id;
                  const vars = (th?.variables?.[theme] || th?.variables?.light || {});
                  const primaryColor = vars['--primary'] || vars['primary'] || '#338edc';
                  const sidebarColor = vars['--sidebar-bg'] || vars['sidebar-bg'] || '#1A1D23';
                  return (
                    <button
                      key={th.id}
                      onClick={() => handleThemeSelect(th.id)}
                      className={`w-full px-3 py-2 flex items-center gap-2.5 hover:bg-surface-hover transition-colors text-left ${
                        isActive ? 'text-[var(--primary)] font-semibold' : 'text-[var(--text-primary)]'
                      }`}
                    >
                      {/* Color swatch */}
                      <div className="flex-shrink-0 w-7 h-5 rounded-[4px] border border-[var(--border-default)] overflow-hidden flex">
                        <div className="w-1/2" style={{ backgroundColor: sidebarColor }} />
                        <div className="w-1/2" style={{ backgroundColor: primaryColor }} />
                      </div>
                      <span className="text-xs flex-1 truncate">{th.name}</span>
                      {isActive && <Check size={14} className="flex-shrink-0" />}
                    </button>
                  );
                })
              )}
              <div className="border-t border-line mt-1 pt-1">
                <button
                  onClick={() => { setThemeMenuOpen(false); navigate('/einstellungen?tab=design'); }}
                  className="w-full px-3 py-1.5 text-[11px] text-[var(--text-secondary)] hover:bg-surface-hover hover:text-[var(--text-primary)] transition-colors text-left"
                >
                  {t('topbar.themeManage')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={handleThemeToggle}
          className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors"
          title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>

        {/* App-Switcher (Admin ↔ Portal) */}
        {canSwitch && (
          <div className="relative" ref={switcherRef}>
            <button
              onClick={() => { setSwitcherOpen(!switcherOpen); setDropdownOpen(false); setNotifOpen(false); }}
              className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors"
              title={t('topbar.switchApp')}
            >
              <LayoutGrid size={16} />
            </button>
            {switcherOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-surface-elevated border border-line-hover rounded-card shadow-elevated z-50 py-1.5 overflow-hidden">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t('topbar.switchApp')}</div>
                <button
                  onClick={() => { setMode('admin'); setSwitcherOpen(false); navigate('/'); }}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 hover:bg-surface-hover transition-colors text-left ${mode === 'admin' ? 'text-[#338edc] font-semibold' : 'text-[var(--text-primary)]'}`}
                >
                  <BarChart3 size={18} />
                  <span className="text-xs">{t('topbar.adminApp')}</span>
                  {mode === 'admin' && <Check size={14} className="ml-auto" />}
                </button>
                <button
                  onClick={() => { setMode('portal'); setSwitcherOpen(false); navigate('/portal'); }}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 hover:bg-surface-hover transition-colors text-left ${mode === 'portal' ? 'text-[#338edc] font-semibold' : 'text-[var(--text-primary)]'}`}
                >
                  <User size={18} />
                  <span className="text-xs">{t('topbar.maPortal')}</span>
                  {mode === 'portal' && <Check size={14} className="ml-auto" />}
                </button>
              </div>
            )}
          </div>
        )}

        {/* #727 — Meine Aufgaben Quick-Access mit Badge */}
        {user?.rolle !== 'jv_partner' && (
          <button
            onClick={() => navigate('/meine-aufgaben')}
            title={t('topbar.my_tasks')}
            className="relative w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-hover transition-colors"
          >
            <CheckCircle2 size={16} />
            {taskCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#338edc] rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                {taskCount > 9 ? '9+' : taskCount}
              </span>
            )}
          </button>
        )}

        {/* Bug-Report Icon (#943) — hidden for portal_kunde + jv_partner */}
        {user?.rolle !== 'jv_partner' && user?.rolle !== 'portal_kunde' && (
          <button
            onClick={() => setBugOpen(true)}
            title={t('bugReport.tooltip') || 'Bug melden'}
            className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-hover transition-colors"
          >
            <Bug size={16} />
          </button>
        )}

        {/* Chat Floating-Button (#1180) — hidden for jv_partner + portal_kunde */}
        {user?.rolle !== 'jv_partner' && user?.rolle !== 'portal_kunde' && (
          <button
            onClick={() => navigate('/chat')}
            title={t('chat.tooltip') || 'Chat öffnen'}
            className="w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-hover transition-colors"
          >
            <MessageCircle size={16} />
          </button>
        )}

        {/* Softphone-Widget (#1422) — nur bei users.softphone_enabled (Profil-Toggle) */}
        {user?.softphone_enabled && user?.rolle !== 'jv_partner' && user?.rolle !== 'portal_kunde' && (
          <SoftphoneWidget />
        )}

        {/* Notifications — hidden for jv_partner */}
        {user?.rolle !== 'jv_partner' && <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setDropdownOpen(false); if (!notifOpen) loadNotifications(); }}
            className="relative w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-hover transition-colors"
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-1 w-80 bg-surface-elevated border border-line-hover rounded-card shadow-elevated z-50 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-line">
                <span className="text-xs font-semibold text-content-secondary">{t('topbar.notifications')}</span>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-[10px] text-primary-400 hover:text-primary-300">{t('topbar.mark_all_read')}</button>
                  )}
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-3 py-8 text-center text-xs text-content-tertiary">{t('topbar.no_notifications')}</div>
                ) : notifications.slice(0, 20).map(n => (
                  <button
                    key={n.id}
                    onClick={() => markOneRead(n)}
                    className={`w-full text-left px-3 py-2.5 border-b border-line-subtle hover:bg-surface-hover flex gap-2.5 transition-colors ${n.gelesen ? 'opacity-50' : ''}`}
                  >
                    <span className="text-sm flex-shrink-0 mt-0.5">{getTypIcon(n.typ)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-content truncate">{n.titel}</span>
                        <span className="text-[9px] text-content-tertiary flex-shrink-0">{timeAgo(n.created_at, t, locale)}</span>
                      </div>
                      {n.nachricht && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 line-clamp-2">{n.nachricht}</div>}
                    </div>
                    {!n.gelesen && <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0 mt-1.5" />}
                  </button>
                ))}
              </div>
              <div className="border-t border-line px-3 py-2 flex items-center justify-between">
                <button
                  onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                  className="text-[10px] text-primary-400 hover:text-primary-300 font-medium"
                >{t('topbar.show_all')}</button>
                <button
                  onClick={() => { setNotifOpen(false); navigate('/profil?tab=benachrichtigungen'); }}
                  className="text-[10px] text-[var(--text-tertiary)] hover:text-primary-400 font-medium"
                >{t('topbar.notification_settings') || 'Einstellungen'}</button>
              </div>
            </div>
          )}
        </div>}

        {/* User menu */}
        <div className="relative" ref={dropRef}>
          <button
            onClick={() => { setDropdownOpen(!dropdownOpen); setNotifOpen(false); }}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-button hover:bg-surface-hover transition-colors"
          >
            <UserAvatar userId={user.id} initials={initials} size={8} />
            <div className="text-left hidden sm:block">
              <div className="text-xs font-medium text-content leading-none">{user.vorname} {user.nachname}</div>
              <div className="text-[10px] text-[var(--text-secondary)] leading-tight">{rolleLabel}</div>
            </div>
            <svg className="w-3 h-3 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-surface-elevated border border-line-hover rounded-card shadow-elevated z-50 overflow-hidden">
              {user?.rolle !== 'jv_partner' && <>
                <button
                  onClick={() => { setDropdownOpen(false); navigate('/profil'); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-content-secondary hover:bg-surface-hover flex items-center gap-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                  {t('topbar.profile')}
                </button>
                <div className="border-t border-line" />
              </>}
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-surface-hover flex items-center gap-2"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                {t('topbar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
      <BugReportModal open={bugOpen} onClose={() => setBugOpen(false)} />
    </>
  );
}
